import { Badge, Box, Container, Group, Image, Paper, Stack, Text, Title } from "@mantine/core";
import { useMemo } from "react";
import { renderContentBlock } from "./renderContentBlock";
import type { ContentBlock } from "../../types/blogTypes";

interface BlogPreviewProps {
  title: string;
  author: string;
  excerpt: string;
  tags: string;
  imagePreview: string;
  sections: ContentBlock[];
}

export function BlogPreview({
  title,
  author,
  excerpt,
  tags,
  imagePreview,
  sections,
}: BlogPreviewProps) {
  const tagList = useMemo(
    () =>
      tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
    [tags],
  );

  return (
    <Container size="md">
      <Stack gap="xl">
        <Box>
          <Stack gap="md">
            <Title order={1}>{title || "Untitled article"}</Title>
            <Group gap="xs" align="center">
              <Text size="sm" c="dimmed">
                {author || "Author"}
              </Text>
              <Text size="sm" c="dimmed">
                &bull;
              </Text>
              <Text size="sm" c="dimmed">
                {new Date().toLocaleDateString()}
              </Text>
            </Group>
            {tagList.length > 0 && (
              <Group gap="xs">
                {tagList.map((tag) => (
                  <Badge key={tag} variant="light">
                    {tag}
                  </Badge>
                ))}
              </Group>
            )}
            {excerpt && (
              <Text size="md" c="dimmed" style={{ fontStyle: "italic" }}>
                {excerpt}
              </Text>
            )}
          </Stack>
        </Box>

        {imagePreview && <Image src={imagePreview} alt="Featured image" radius="md" fit="cover" />}

        {sections.length === 0 ? (
          <Paper
            withBorder
            radius="md"
            p="xl"
            style={{ backgroundColor: "var(--oculera-surface-alt)" }}
          >
            <Text ta="center" c="dimmed">
              No sections yet. Go to the Editor tab to add content.
            </Text>
          </Paper>
        ) : (
          <Stack gap="xl">
            {sections.map((section, index) => (
              <Box key={`${section.type}-${index}`}>{renderContentBlock(section, index)}</Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
